import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  FaShieldAlt,
  FaCogs,
  FaChartLine,
  FaSyncAlt,
  FaBalanceScale,
  FaHandshake,
  FaUserShield,
  FaEye,
  FaGavel,
  FaCheckCircle,
  FaClipboardList,
  FaGlobeAmericas,
  FaPuzzlePiece,
  FaUsers,
  FaServer,
  FaBullseye,
  FaLayerGroup,
  FaClock,
  FaTools,
  FaUserTie,
  FaMapMarkerAlt,
  FaChevronDown,
  FaExternalLinkAlt,
  FaQuoteLeft,
} from 'react-icons/fa';
import { RESPONSIBLE_AI, RESPONSIBLE_AI_GOVERNANCE } from '../config/content';
import styles from './ResponsibleAI.module.css';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';

/* ── Reveal wrapper (same pattern as Home) ── */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const TAB_ICONS: Record<string, React.ReactNode> = {
  'ai-ethics': <FaBalanceScale />,
  'ai-governance': <FaShieldAlt />,
  'ai-principles': <FaGavel />,
  'strategic-imperatives': <FaGlobeAmericas />,
};

const TAB_DESCRIPTIONS: Record<string, string> = {
  'ai-ethics': 'Fairness, transparency, privacy, and human-centred design.',
  'ai-governance': 'The 5P framework governing every AI system end-to-end.',
  'ai-principles': 'Accuracy, accountability, proportionality, and improvement.',
  'strategic-imperatives': 'Sovereignty, open standards, collaboration, and resilience.',
};

const ITEM_ICONS: Record<string, React.ReactNode[]> = {
  'ai-ethics': [<FaHandshake />, <FaEye />, <FaUserShield />, <FaUsers />],
  'ai-governance': [<FaGavel />, <FaShieldAlt />, <FaCogs />, <FaChartLine />, <FaSyncAlt />],
  'ai-principles': [<FaClipboardList />, <FaEye />, <FaBalanceScale />, <FaSyncAlt />],
  'strategic-imperatives': [<FaServer />, <FaPuzzlePiece />, <FaUsers />, <FaShieldAlt />],
};

const FRAMEWORK_DEF_ICONS: Record<string, React.ReactNode> = {
  WHY: <FaBullseye />,
  WHAT: <FaLayerGroup />,
  WHEN: <FaClock />,
  HOW: <FaTools />,
  WHO: <FaUserTie />,
  WHERE: <FaMapMarkerAlt />,
};

const PILLAR_ICONS: Record<string, React.ReactNode> = {
  Policy: <FaGavel />,
  Protection: <FaShieldAlt />,
  Platform: <FaCogs />,
  Proof: <FaChartLine />,
  Persistence: <FaSyncAlt />,
};

/* Renders [label](url) segments in content strings as external links */
const LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)/g;

const trackLinkClick = (text: string, url: string, location: string) =>
  trackEvent('governance_link_click', { link_text: text, link_url: url, link_location: location });

const RichText: React.FC<{ text: string; location: string }> = ({ text, location }) => {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of Array.from(text.matchAll(LINK_PATTERN))) {
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    parts.push(
      <a
        key={index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.inlineLink}
        onClick={() => trackLinkClick(match[1], match[2], location)}
      >
        {match[1]}
      </a>
    );
    last = index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const ResponsibleAI: React.FC = () => {
  const [activeTab, setActiveTab] = useState(RESPONSIBLE_AI.tabs[0].key);
  const activeData = RESPONSIBLE_AI.tabs.find((t) => t.key === activeTab)!;
  const icons = ITEM_ICONS[activeTab] || [];
  const governance = activeTab === RESPONSIBLE_AI_GOVERNANCE.key ? RESPONSIBLE_AI_GOVERNANCE : null;
  const isGovernance = governance !== null;
  const [openPillars, setOpenPillars] = useState<Set<string>>(() => new Set(['Policy']));
  const allPillarsOpen = governance !== null && openPillars.size === governance.items.length;

  const selectTab = (key: string) => {
    if (key !== activeTab) trackEvent('responsible_ai_tab', { tab: key });
    setActiveTab(key);
  };

  const togglePillar = (title: string) => {
    trackEvent('governance_pillar_toggle', { pillar: title, action: openPillars.has(title) ? 'close' : 'open' });
    setOpenPillars((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const toggleAllPillars = () => {
    trackEvent('governance_expand_all', { action: allPillarsOpen ? 'collapse' : 'expand' });
    setOpenPillars(allPillarsOpen || !governance ? new Set() : new Set(governance.items.map((p) => p.title)));
  };

  return (
    <main className={styles.page}>
      <SEO path="/responsible-ai" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.hero}>
          <motion.div
            className={styles.heroInner}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.heroContent}>
              <div className={styles.heroText}>
                <div className={styles.heroBadge}>
                  <div className={styles.badgeDot} />
                  <span>Responsible AI</span>
                </div>
                <h1>
                  Responsible AI at <em>JurisTab</em>
                </h1>
                <p className={styles.heroSub}>Our commitment to ethical, transparent, and accountable AI in legal technology.</p>
              </div>
              <div className={styles.heroImageWrap}>
                <img
                  src="/images/responsible-ai/hero-banner.png"
                  alt="Responsible AI"
                  className={styles.heroImage}
                />
              </div>
            </div>
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Sidebar + Content ── */}
      <section className={styles.mainLayout}>
        {/* Left Panel */}
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav}>
            {RESPONSIBLE_AI.tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ''}`}
                  onClick={() => selectTab(tab.key)}
                >
                  <span className={styles.sidebarIcon}>{TAB_ICONS[tab.key]}</span>
                  <span className={styles.sidebarText}>
                    <span className={styles.sidebarLabel}>{tab.label}</span>
                    <span className={styles.sidebarDesc}>{TAB_DESCRIPTIONS[tab.key]}</span>
                  </span>
                  {isActive && <div className={styles.sidebarActiveIndicator} />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right Content */}
        <div className={styles.content}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Header */}
              <div className={styles.sectionTag}>{activeData.label}</div>
              <h2 className={styles.contentTitle}>{activeData.title}</h2>
              <p className={styles.contentSub}>{activeData.subtitle}</p>

              {/* Tab Image */}
              <div className={`${styles.imageContainer} ${!isGovernance ? styles.imageContainerCompact : ''}`}>
                <img
                  src={activeData.image}
                  alt={activeData.label}
                  className={styles.tabImage}
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className={styles.imagePlaceholder} style={{ display: 'none' }}>
                  <span className={styles.imagePlaceholderIcon}>{TAB_ICONS[activeTab]}</span>
                  <span className={styles.imagePlaceholderText}>{activeData.label} — Illustration</span>
                </div>
              </div>

              {/* Governance-specific: vision statement */}
              {governance && (
                <div className={styles.visionBanner}>
                  <FaQuoteLeft className={styles.visionQuote} />
                  <div>
                    <span className={styles.visionLabel}>Framework Vision</span>
                    <p className={styles.visionText}>{governance.vision}</p>
                  </div>
                </div>
              )}

              {/* Intro */}
              <p className={styles.introText}>{activeData.intro}</p>

              {/* Governance-specific: second intro paragraph */}
              {governance && <p className={styles.introText}>{governance.frameworkIntro}</p>}

              {/* Governance-specific: Framework Definition */}
              {governance && (
                <Reveal>
                  <h3 className={styles.governanceSectionTitle}>Framework Definition</h3>
                  <div className={styles.frameworkDefGrid}>
                    {governance.frameworkDefinition.map((def) => (
                      <div className={styles.frameworkDefItem} key={def.label}>
                        <div className={styles.frameworkDefIcon}>
                          {FRAMEWORK_DEF_ICONS[def.label] || <FaCheckCircle />}
                        </div>
                        <div className={styles.frameworkDefContent}>
                          <span className={styles.frameworkDefLabel}>{def.label}</span>
                          <p>{def.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              )}

              {/* Governance-specific: The Five Pillars (collapsible) */}
              {governance && (
                <Reveal>
                  <div className={styles.governanceSectionHead}>
                    <h3 className={styles.governanceSectionTitle}>The Five Pillars</h3>
                    <button type="button" className={styles.expandAll} onClick={toggleAllPillars}>
                      {allPillarsOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>
                  <div className={styles.pillarsContainer}>
                    {governance.items.map((item, i) => {
                      const isOpen = openPillars.has(item.title);
                      const panelId = `pillar-panel-${item.title.toLowerCase()}`;
                      return (
                        <div
                          className={`${styles.pillarSection} ${isOpen ? styles.pillarSectionOpen : ''}`}
                          key={item.title}
                        >
                          <button
                            type="button"
                            className={styles.pillarHeader}
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() => togglePillar(item.title)}
                          >
                            <div className={styles.pillarIcon}>
                              {PILLAR_ICONS[item.title] || <FaCheckCircle />}
                            </div>
                            <div className={styles.pillarHeading}>
                              <span className={styles.pillarIndex}>P{i + 1}</span>
                              <h4 className={styles.pillarTitle}>{item.title}</h4>
                              <span className={styles.pillarCategory}>{item.category}</span>
                              <p className={styles.pillarSummary}>{item.description}</p>
                            </div>
                            <FaChevronDown className={styles.pillarChevron} aria-hidden="true" />
                          </button>
                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                id={panelId}
                                className={styles.pillarPanel}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                              >
                                <div className={styles.subItemsGrid}>
                                  {item.subItems.map((sub) => (
                                    <div className={styles.subItem} key={sub.title}>
                                      <h5>{sub.title}</h5>
                                      <p>
                                        <RichText text={sub.description} location={item.title} />
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </Reveal>
              )}

              {/* Governance-specific: AI Council */}
              {governance && (
                <Reveal>
                  <h3 className={`${styles.governanceSectionTitle} ${styles.governanceSectionSpaced}`}>
                    AI Council Governance
                  </h3>
                  <p className={styles.sectionLead}>{governance.council.intro}</p>
                  <div className={styles.councilTableWrap}>
                    <table className={styles.councilTable}>
                      <thead>
                        <tr>
                          <th scope="col">Role</th>
                          <th scope="col">Responsibility</th>
                          <th scope="col">Cadence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {governance.council.roles.map((r) => (
                          <tr key={r.role}>
                            <th scope="row" data-label="Role">{r.role}</th>
                            <td data-label="Responsibility">{r.responsibility}</td>
                            <td data-label="Cadence">{r.cadence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.keyPrinciple}>
                    <span className={styles.keyPrincipleLabel}>Key Principle</span>
                    <p>
                      <RichText text={governance.council.keyPrinciple} location="Key Principle" />
                    </p>
                  </div>
                </Reveal>
              )}

              {/* Governance-specific: Team workflow */}
              {governance && (
                <Reveal>
                  <h3 className={`${styles.governanceSectionTitle} ${styles.governanceSectionSpaced}`}>
                    Quick Reference: Team Workflow
                  </h3>
                  <ol className={styles.workflowList}>
                    {governance.workflow.map((step, i) => (
                      <li className={styles.workflowStep} key={step}>
                        <span className={styles.workflowNum}>Step {i + 1}</span>
                        <span className={styles.workflowText}>{step}</span>
                      </li>
                    ))}
                  </ol>
                </Reveal>
              )}

              {/* Governance-specific: References */}
              {governance && (
                <Reveal>
                  <h3 className={`${styles.governanceSectionTitle} ${styles.governanceSectionSpaced}`}>
                    Industry Framework Alignment
                  </h3>
                  <p className={styles.sectionLead}>{governance.references.intro}</p>
                  {governance.references.groups.map((group) => (
                    <div className={styles.refGroup} key={group.title}>
                      <h4 className={styles.refGroupTitle}>{group.title}</h4>
                      <div className={styles.refGrid}>
                        {group.items.map((ref) => (
                          <a
                            key={ref.name}
                            href={ref.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.refCard}
                            onClick={() => trackLinkClick(ref.name, ref.link, 'References')}
                          >
                            <span className={styles.refName}>
                              {ref.name}
                              <FaExternalLinkAlt className={styles.refIcon} aria-hidden="true" />
                            </span>
                            <span className={styles.refDesc}>{ref.description}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </Reveal>
              )}

              {/* Standard cards for non-governance tabs */}
              {!isGovernance && (
                <Reveal className={styles.cardsGrid}>
                  {activeData.items.map((item, i) => (
                    <div className={styles.card} key={i}>
                      <div className={styles.cardIcon}>{icons[i] || <FaCheckCircle />}</div>
                      <div className={styles.cardBody}>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </Reveal>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── CTA Banner ── */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>
              Trust Starts with<br /><em>Transparency</em>
            </h2>
            <p>{RESPONSIBLE_AI.cta.subtitle}</p>
          </div>
          <div className={styles.ctaActions}>
            <a
              href={RESPONSIBLE_AI.cta.ctaPrimaryLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnLarge}
              style={{ textAlign: 'center' }}
            >
              {RESPONSIBLE_AI.cta.ctaPrimary}
            </a>
            <Link
              to={RESPONSIBLE_AI.cta.ctaSecondaryLink}
              className={styles.btnGhost}
              style={{ textAlign: 'center' }}
            >
              {RESPONSIBLE_AI.cta.ctaSecondary}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default ResponsibleAI;
